// common
import {
  getModule,
  VuexModule,
  Mutation,
  Action,
  Module,
} from "vuex-module-decorators";
import store from "@/store";

// api
import { getUser, patchUser } from "@/api/user/user";

// modules
import AppModule from "@/store/modules/app";

// interfaces
import { IUserState, UserRole } from "@/interfaces/store/IUserState";
import {
  IUser,
  IUserGroups,
  IUserPatchResponse,
} from "@/interfaces/responses/user";
import { IUpdateUserInfo } from "@/interfaces/_shared/user";

// helpers
import NotificationService from "@/services/notification.service";
import _get from "lodash/get";
import { checkUserSocialSecurityNumber } from "@/utils/helpers/auth";

@Module({ dynamic: true, namespaced: true, store, name: "User" })
class User extends VuexModule implements IUserState {
  public loading = false;
  public user = {} as IUser;
  public userId: number | null = null;
  public acceptedRoles = [
    UserRole.ROLE_OWNER,
    UserRole.ROLE_SELLER,
    UserRole.ROLE_MANAGER,
    UserRole.ROLE_SELLER_MANAGER,
    UserRole.ROLE_RESTRICTED_MANAGER,
    UserRole.ROLE_SELLER_NO_DISCOUNT,
    UserRole.ROLE_POS_SELLER,
    UserRole.ROLE_POS_SELLER_MANAGER,
  ];

  /**
   * Get user data
   *
   * @return {IUser}
   */
  public get getUser(): IUser {
    return this.user || ({} as IUser);
  }

  /**
   * Get user id
   *
   * @return number | null
   */
  public get getUserId(): number | null {
    return this.userId;
  }

  /**
   * Has user
   *
   * @return boolean
   */
  public get hasUser() {
    return this.userId ? true : false;
  }

  /**
   * Whether or not the user is an admin
   *
   * @return boolean
   */
  public get isAdmin(): boolean {
    return this.user.isAdmin;
  }

  public get isSellerNoDiscount(): boolean {
    const sellerNoDiscount = this.user.userGroups.filter(
      (ug) =>
        ug.role === UserRole.ROLE_SELLER_NO_DISCOUNT &&
        ug.group.id === this.getUserGroupId
    );
    if (sellerNoDiscount && sellerNoDiscount.length) {
      return true;
    }
    return false;
  }

  public get canSeeAllPOSConfigs(): boolean {
    const userCanSeeAllConfigs = this.user.userGroups.filter(
      (ug) =>
        (ug.role === UserRole.ROLE_OWNER ||
          ug.role === UserRole.ROLE_MANAGER) &&
        ug.group.id === this.getUserGroupId
    );
    if (userCanSeeAllConfigs && userCanSeeAllConfigs.length) {
      return true;
    }
    return false;
  }

  /**
   * Get all user groups info from user data
   *
   * @return {IUserGroups[]}
   */
  get getUserGroups(): IUserGroups[] {
    return this.getUser.userGroups || [];
  }

  /**
   * Get user groups that can be selected on the POS
   */
  get selectableUserGroups(): IUserGroups[] {
    return (this.getUser.userGroups || []).filter((ug) => {
      if (this.isAdmin) {
        return true;
      }

      return !!(
        ug.id &&
        this.acceptedRoles.includes(ug.role) &&
        ug.state === 1 &&
        ug.group.id
      );
    });
  }

  // Indicates whether a user belongs to multiple groups or not
  get userHasMultipleGroups(): boolean {
    return this.selectableUserGroups.length > 1;
  }

  // Indicates whether a user is actually a colleague or no
  get isColleague(): boolean {
    let isColleague = false;
    const groups = this.getUserGroups.map((g) => g.group.id);
    const internalGroups = groups.filter((g) => g === 19066 || g === 1);
    if (internalGroups && internalGroups.length) {
      isColleague = true;
    }
    return isColleague;
  }

  /**
   * The user group is the one where the id exists with the role 'Owner, Manager, Seller' and a state 'Confirmed'
   * OR he is an admin and he wants to have access to the gid stated in the query parameter
   */
  public get getUserGroupId(): number | null {
    const gid = AppModule.gid;
    if (gid) {
      // So the user stated a group id as query parameter to access to

      if (this.isAdmin) {
        return Number(gid);
      } else {
        const userGroup = this.getUserGroups.find(
          (ug) => ug.group.id === Number(gid)
        );

        if (!userGroup) return null;

        if (this.acceptedRoles.includes(userGroup?.role)) {
          return Number(gid);
        } else {
          return null;
        }
      }
    } else {
      if (this.isAdmin) {
        return null;
      }
      return (
        this.getUserGroups.find(
          (ug: IUserGroups) =>
            ug.id &&
            this.acceptedRoles.includes(ug.role) &&
            ug.state === 1 &&
            ug.group.id
        )?.group.id || null
      );
    }
  }

  get isAdminUserWithoutGroup() {
    return this.isAdmin && !this.getUserGroupId;
  }

  /**
   * Get selected group info from user data
   *
   * @return {IUserGroups}
   */
  get getUserSelectedGroupInfo(): IUserGroups {
    return (
      this.getUserGroups.find(
        (ug: IUserGroups) => ug.group.id === this.getUserGroupId
      ) || ({} as IUserGroups)
    );
  }

  /**
   * Get "otherData" of the selected group
   *
   * @returns {object}
   */
  public get getGroupOtherData(): any {
    const settings =
      _get(this, "getUserSelectedGroupInfo.otherData") ||
      '{"welcome-page":null}';
    return JSON.parse(settings);
  }

  /**
   * Getter for advancedSettings
   *
   * @returns {object}
   */
  public get getAdvancedSettings(): any {
    return this.getUser.advancedSettings || {};
  }

  /**
   * Returns the first primary email of the user
   */
  public get getUserEmail(): string {
    let email = "";
    if (this.user && this.user.emails && this.user.emails.length) {
      const primaryEmail = this.user.emails.find((e) => e.isPrimary);
      if (primaryEmail) {
        email = primaryEmail.emailAddress;
      } else {
        email = this.user.emails[0].emailAddress;
      }
    }
    return email;
  }

  @Action({ commit: "SET_USER", rawError: true })
  public async fetchUser(): Promise<IUser> {
    try {
      const user = await getUser();
      this.SET_USERId(user.id);
      // Default to the app's language if user.language is null
      if (user.language?.code) {
        // @ts-ignore
        AppModule.SetLanguage(user.language.code);
      }
      // Catches a Belgian operator whose social security number was removed or
      // changed server-side after they logged in.
      checkUserSocialSecurityNumber(user);
      return user;
    } catch (error) {
      NotificationService.showGeneralResponseError();

      throw new Error(error as string);
    }
  }

  /**
   * Update user info 'PATCH_user'
   *
   * @param {IUpdateUserInfo} payload: params for request
   * @returns {Promise<IUserPatchResponse>}
   */
  @Action({ rawError: true })
  public async patchInfo(
    payload: IUpdateUserInfo
  ): Promise<IUserPatchResponse> {
    return await patchUser(payload);
  }

  @Mutation
  private SET_USER(payload: IUser) {
    this.user = payload;
  }

  @Mutation
  private SET_USERId(payload: number) {
    this.userId = payload;
  }
}

const UserModule = getModule(User);
export default UserModule;
